import type { Company } from "@/api/company.service";
import type { GenericModule, Module } from "@/api/module.service";
import { getModulesByUserCompany } from "@/api/user.service";
import { Business, CheckCircle, ExpandMore, RadioButtonUnchecked } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Grid, Paper, Stack, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";

interface CompanyModuleParams {
  company: Company,
  userId: string,
  clientModules: GenericModule[],
  allModules: Module[],
  value?: string[],
  onChange: (moduleIds: string[]) => void,
}

const CompanyModuleSection = ({ company, userId, clientModules, allModules, value, onChange }: CompanyModuleParams) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModules = async () => {
      // Skip fetch if userId not yet set (new user being created)
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getModulesByUserCompany({ company_id: company.id, user_id: userId });
        onChange(data.map(m => m.module_id));
      } catch (error) {
        console.error("Error fetching modules", error);
        onChange([]);
      } finally {
        setLoading(false);
      }
    };
    fetchModules();
  }, [company.id, userId]);

  const enabledModuleIds = value ?? [];

  const handleToggle = (module: GenericModule, checked: boolean) => {
    const newIds = checked
      ? [...enabledModuleIds, module.id]
      : enabledModuleIds.filter(id => id !== module.id);
    onChange(newIds);
  };

  const handleBulkAction = (e: React.MouseEvent, action: 'select' | 'unselect') => {
    e.stopPropagation();
    onChange(action === 'select' ? clientModules.map(m => m.id) : []);
  };

  if (loading) return <CircularProgress size={20} sx={{ m: 2 }} />;

  return (
    <Accordion variant="outlined" sx={{ mb: 1 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Business color="primary" fontSize="small" />
          <Typography sx={{ fontWeight: 600 }}>{company.name}</Typography>
        </Box>

        {/* Bulk Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
          <Button
            component="span"
            size="small"
            variant="outlined"
            onClick={(e) => handleBulkAction(e, 'select')}
            startIcon={<CheckCircle />}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Todos
          </Button>
          <Button
            component="span"
            size="small"
            variant="outlined"
            color="error"
            onClick={(e) => handleBulkAction(e, 'unselect')}
            startIcon={<RadioButtonUnchecked />}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Nenhum
          </Button>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ bgcolor: 'action.hover' }}>
        <Grid container spacing={2}>
          {clientModules.length === 0 ? (
            <Grid sx={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 0.5 }}>
                Nenhum módulo disponível para esta empresa.
              </Typography>
            </Grid>
          ) : (clientModules.map((module) => {
            const isEnabled = enabledModuleIds.includes(module.id);
            const label = allModules.find(m => m.id === module.id)?.label || module.id;

            return (
              <Grid sx={{ xs: 12, sm: 6, md: 4 }} key={module.id}>
                <Paper variant="outlined" sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{label}</Typography>
                  <Switch
                    size="small"
                    checked={isEnabled}
                    onChange={(_, checked) => handleToggle(module, checked)}
                  />
                </Paper>
              </Grid>
            );
          }))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default CompanyModuleSection;
